import { motion } from "framer-motion";
import { CircleHelp } from "lucide-react";
import type { ComponentType, ReactNode } from "react";

import { cn } from "../../lib/utils";
import { Tooltip } from "../ui/tooltip";

interface WidgetFrameProps {
  icon?: ComponentType<{ className?: string }>;
  title?: string;
  subtitle?: string;
  children?: ReactNode;
  footer?: ReactNode;
  className?: string;
  "data-widget-type"?: string;
}

export function WidgetFrame({
  children,
  className,
  footer,
  icon: Icon,
  subtitle,
  title,
  "data-widget-type": dataWidgetType,
}: WidgetFrameProps) {
  const hasHeader = Boolean(Icon || title || subtitle);

  return (
    <motion.section
      className={cn(
        "rounded-3xl border border-border-soft bg-card p-4 shadow-sm",
        className,
      )}
      data-slot="advisor-widget"
      data-widget-type={dataWidgetType}
      layout
      transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
        layout: { duration: 0.3 },
      }}
    >
      {hasHeader ? (
        <motion.div
          className="flex items-start gap-3"
          layout
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          {Icon ? (
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-2xl bg-primary-soft text-primary-dark">
              <Icon className="h-5 w-5" />
            </span>
          ) : null}
          <div className="min-w-0 flex-1">
            {title ? (
              <p className="text-sm font-semibold text-foreground">{title}</p>
            ) : null}
            {subtitle ? (
              <p className="mt-0.5 text-xs text-muted-foreground">{subtitle}</p>
            ) : null}
          </div>
        </motion.div>
      ) : null}
      {children ? (
        <motion.div
          className={cn(hasHeader && "mt-4")}
          layout
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          {children}
        </motion.div>
      ) : null}
      {footer ? (
        <motion.div
          className="mt-4 border-t border-border-soft pt-3 text-xs text-muted-foreground"
          layout
          transition={{ duration: 0.24, ease: [0.22, 1, 0.36, 1] }}
        >
          {footer}
        </motion.div>
      ) : null}
    </motion.section>
  );
}

export function WidgetPlaceholderRow({
  label,
  tooltip,
  value,
}: {
  label: string;
  tooltip?: ReactNode;
  value?: ReactNode;
}) {
  return (
    <motion.div
      className="flex items-center justify-between py-1.5 text-sm"
      layout
      transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
    >
      <span className="inline-flex min-w-0 items-center gap-1.5 text-muted-foreground">
        <span className="min-w-0">{label}</span>
        {tooltip ? (
          <Tooltip
            content={
              <span className="block max-w-64 leading-5">{tooltip}</span>
            }
          >
            <button
              aria-label={`About ${label}`}
              className="relative inline-flex h-6 w-6 items-center justify-center rounded-full text-muted-foreground transition-colors after:absolute after:-inset-2 hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              type="button"
            >
              <CircleHelp aria-hidden="true" className="h-3.5 w-3.5" />
            </button>
          </Tooltip>
        ) : null}
      </span>
      <span className="font-bold tabular-nums text-foreground">
        {value ?? <span className="text-muted-foreground">—</span>}
      </span>
    </motion.div>
  );
}
